import { FC, useMemo } from "react";

import { QuestionResults, Section } from "@/types/resources";
import ABTestReport, { ABTestReportData } from "./ABTestReport";
import { itemsFromQuestion } from "./_itemsFromQuestion";
import { countUniqueRespondents } from "./_respondentCount";

interface ABTestSectionResultsProps {
  data: QuestionResults[];
  section: Section;
}

/* ── Cooker ─────────────────────────────────────────────── */

/** A/B sections are templated as a single-choice question whose options
    (text or one image each) are the variants. We tally choices off the first
    single-choice question we find and take the option list from that same
    question, so labels match what respondents actually saw. */
function cookABTestData(data: QuestionResults[] | undefined): ABTestReportData {
  const choiceQuestion = (data ?? []).find(
    (r) => r?.question?.type === "single_choice"
  );
  const options = itemsFromQuestion(choiceQuestion?.question).map((item) => ({
    ...item,
    themes: null,
  }));

  const votes = options.map(() => 0);

  if (choiceQuestion) {
    (choiceQuestion.groups ?? []).forEach((g) => {
      (g.answers ?? []).forEach((a) => {
        if (
          a.answer.type === "single_choice" &&
          typeof a.answer.choice === "number" &&
          a.answer.choice >= 0 &&
          a.answer.choice < votes.length
        ) {
          votes[a.answer.choice] += 1;
        }
      });
    });
  }

  return { options, respondentCount: countUniqueRespondents(data), votes };
}

/* ── Component ──────────────────────────────────────────── */

const ABTestSectionResults: FC<ABTestSectionResultsProps> = ({ data }) => {
  const reportData = useMemo(() => cookABTestData(data), [data]);
  return <ABTestReport data={reportData} />;
};

export default ABTestSectionResults;
